import React, { useCallback } from 'react';

import { InfoHint } from 'components/common/InfoHint';
import Styled from './RoutesEditor.styled';

const {
  StyledRouteCard,
  StyledRow,
  StyledFieldLabel,
  StyledConditionLabel,
  StyledFullWidthInput,
  StyledTextarea,
  StyledRemoveButton,
  StyledAddButton,
  StyledMuted,
} = Styled;

export interface Route {
  key: string;
  conditionExpression: string;
}

interface OwnProps {
  routes: Route[];
  onChange: (next: Route[]) => void;
}

interface RouteRowProps {
  index: number;
  route: Route;
  onKeyChange: (index: number, key: string) => void;
  onConditionChange: (index: number, conditionExpression: string) => void;
  onRemove: (index: number) => void;
}

/**
 * A single route entry. Extracted so each row gets its own stable
 * per-index handlers; without this, the parent's `.map` would allocate
 * three fresh closures per route per render. Inlined here because it's
 * only used by RoutesEditor (mirrors EdgeRow in ConnectionsList).
 */
const RouteRow = ({ index, route, onKeyChange, onConditionChange, onRemove }: RouteRowProps) => {
  const handleKeyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onKeyChange(index, event.target.value);
    },
    [index, onKeyChange],
  );

  const handleConditionChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onConditionChange(index, event.target.value);
    },
    [index, onConditionChange],
  );

  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <StyledRouteCard>
      <StyledRow>
        <StyledFieldLabel htmlFor={`route-key-${index}`}>Key</StyledFieldLabel>
        <StyledFullWidthInput
          id={`route-key-${index}`}
          type='text'
          value={route.key}
          onChange={handleKeyChange}
        />
      </StyledRow>
      <StyledRow>
        <StyledConditionLabel htmlFor={`route-condition-${index}`}>
          Condition
          <InfoHint
            text='Scriban expression. Reference step outputs with {{ stepId.outputName }} or job inputs with {{ input.fieldName }}.'
            title='Condition'
          />
        </StyledConditionLabel>
        <StyledTextarea
          id={`route-condition-${index}`}
          value={route.conditionExpression}
          rows={2}
          onChange={handleConditionChange}
        />
      </StyledRow>
      <StyledRemoveButton type='button' onClick={handleRemove} className='btn-ghost btn-sm'>
        Remove
      </StyledRemoveButton>
    </StyledRouteCard>
  );
};

const MemoRouteRow = React.memo(RouteRow);

const RoutesEditor = ({ routes, onChange }: OwnProps) => {
  const handleKeyChange = useCallback(
    (index: number, key: string) => {
      const updated = routes.map((r, i) => (i === index ? { ...r, key } : r));
      onChange(updated);
    },
    [routes, onChange],
  );

  const handleConditionChange = useCallback(
    (index: number, conditionExpression: string) => {
      const updated = routes.map((r, i) => (i === index ? { ...r, conditionExpression } : r));
      onChange(updated);
    },
    [routes, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(routes.filter((_, i) => i !== index));
    },
    [routes, onChange],
  );

  const handleAdd = useCallback(() => {
    onChange([...routes, { conditionExpression: '', key: '' }]);
  }, [routes, onChange]);

  return (
    <>
      {routes.length === 0 && <StyledMuted>No routes defined.</StyledMuted>}
      {routes.map((route, index) => (
        // REASON: route.key can be empty / non-unique; use composite key
        // index suffix to maintain stable identity across renders.
        <MemoRouteRow
          key={`${route.key ?? ''}-${index}`}
          index={index}
          route={route}
          onKeyChange={handleKeyChange}
          onConditionChange={handleConditionChange}
          onRemove={handleRemove}
        />
      ))}
      <StyledAddButton type='button' onClick={handleAdd} className='btn-sm'>
        Add Route
      </StyledAddButton>
    </>
  );
};

export default React.memo(RoutesEditor);
